import { useState, useEffect, useMemo } from "react";
import { supabase } from "./supabase";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, LineChart, Line, ComposedChart,
  ReferenceLine, LabelList
} from "recharts";

// ─── DATA CLEANING ──────────────────────────────────────────────────────────
function cleanRow(r) {
  const parseNum = (v) => {
    if (!v || v === "-" || v === "") return null;
    return parseFloat(String(v).replace(",", "."));
  };
  const valor = parseNum(r.valor_numerico);
  const limMin = parseNum(r.limite_min);
  const limMax = parseNum(r.limite_max);
  const conf = r.conformidade;
  const isConforme = conf === "Conforme" ? true : conf === "Não Conforme" ? false : null;
  let pctLimite = null;
  let pctExcedido = 0;
  const param = r.parametro;
  if (param === "pH" && limMin != null && limMax != null && valor != null) {
    if (valor < limMin) { pctLimite = (valor / limMin) * 100; pctExcedido = ((limMin - valor) / limMin) * 100; }
    else if (valor > limMax) { pctLimite = (valor / limMax) * 100; pctExcedido = ((valor - limMax) / limMax) * 100; }
    else { pctLimite = 100; pctExcedido = 0; }
  } else if (param === "Oxigênio Dissolvido" && limMin != null && valor != null) {
    pctLimite = (valor / limMin) * 100;
    if (valor < limMin) pctExcedido = ((limMin - valor) / limMin) * 100;
  } else if (limMax != null && valor != null) {
    pctLimite = (valor / limMax) * 100;
    if (valor > limMax) pctExcedido = ((valor - limMax) / limMax) * 100;
  }
  let clima = r.condicoes_climaticas || "";
  if (clima === "CONAMA 357/2005") clima = "Não informado";
  return {
    numero_relatorio: r.numero_relatorio || "",
    data_amostragem: r.data_amostragem || "",
    mes: r.mes || "",
    ano: parseInt(r.ano) || 0,
    ponto_coleta: r.ponto_coleta || "",
    parametro: param || "",
    valor, unidade: r.unidade || "",
    resultado: r.resultado || "",
    limite_min: limMin, limite_max: limMax,
    conformidade: conf,
    is_conforme: isConforme,
    legislacao: r.legislacao_referencia || "",
    clima, pct_limite: pctLimite, pct_excedido: pctExcedido,
    tipo_matriz: r.tipo_matriz || "",
    reanalise: r.reanalise || "",
    custo: r.custo || "",
    empresa: r.empresa || "",
    endereco: r.endereco || "",
  };
}

// ─── HELPERS ────────────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return "—";
  const s = String(d);
  if (s.includes("-")) { const [y, m, day] = s.split("-"); return `${day}/${m}/${y}`; }
  return s;
};
const fmtNum = (n, dec = 2) => {
  if (n == null || isNaN(n)) return "—";
  return n.toLocaleString("pt-BR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
};

const COLORS = {
  primary: "#1B2A4A", green: "#1E8449", red: "#C0392B",
  bgLight: "#F5F5F5", white: "#FFFFFF", yellow: "#FEF9E7",
  redLight: "#FDECEA", greenLight: "#E8F5E9", blueMid: "#2E86C1",
};

// ─── URL ROUTING ────────────────────────────────────────────────────────────
function getClientToken() {
  const path = window.location.pathname;
  const match = path.match(/\/view\/([a-f0-9-]{36})/i);
  if (match) return match[1];
  const params = new URLSearchParams(window.location.search);
  return params.get("token") || params.get("cliente") || null;
}

// ─── DROPDOWN COMPONENT ─────────────────────────────────────────────────────
function MultiSelect({ label, options, selected, onChange, multi = true }) {
  const [open, setOpen] = useState(false);
  const toggle = (val) => {
    if (!multi) { onChange([val]); setOpen(false); return; }
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  const displayText = selected.length === 0 ? "Todos" : selected.length <= 2 ? selected.join(", ") : `${selected.length} selecionados`;
  return (
    <div style={{ position: "relative", minWidth: 140 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: "#8899AA", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div onClick={() => setOpen(!open)} style={{ padding: "6px 10px", background: COLORS.white, border: "1px solid #D5DDE5", borderRadius: 6, cursor: "pointer", fontSize: 13, color: COLORS.primary, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, userSelect: "none" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120 }}>{displayText}</span>
        <span style={{ fontSize: 10, opacity: 0.5 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: COLORS.white, border: "1px solid #D5DDE5", borderRadius: 6, marginTop: 2, zIndex: 999, maxHeight: 220, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
          {multi && <div onClick={() => onChange([])} style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", borderBottom: "1px solid #eee", color: "#888", fontStyle: "italic" }}>Limpar filtro</div>}
          {options.map(opt => (
            <div key={opt} onClick={() => toggle(opt)} style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer", background: selected.includes(opt) ? "#EBF5FB" : "transparent", display: "flex", alignItems: "center", gap: 6 }}>
              {multi && <span style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${selected.includes(opt) ? COLORS.blueMid : "#CBD5E0"}`, background: selected.includes(opt) ? COLORS.blueMid : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "white", flexShrink: 0 }}>{selected.includes(opt) ? "✓" : ""}</span>}
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── KPI CARD ───────────────────────────────────────────────────────────────
function KpiCard({ icon, title, value, subtitle, bg, textColor, borderColor }) {
  return (
    <div style={{ background: bg || COLORS.white, borderRadius: 10, padding: "16px 18px", minWidth: 145, flex: "1 1 145px", border: borderColor ? `2px solid ${borderColor}` : "1px solid #E8ECF0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
      <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600, color: textColor || "#8899AA", opacity: 0.85, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: textColor || COLORS.primary, lineHeight: 1.1 }}>{value}</div>
      {subtitle && <div style={{ fontSize: 11, color: textColor || "#8899AA", opacity: 0.8, marginTop: 4 }}>{subtitle}</div>}
    </div>
  );
}

// ─── PAGE 1: EXECUTIVE ──────────────────────────────────────────────────────
function PageExecutive({ data }) {
  const totalAnalises = data.length;
  const conformes = data.filter(d => d.is_conforme === true).length;
  const naoConformes = data.filter(d => d.is_conforme === false).length;
  const withConf = data.filter(d => d.is_conforme !== null);
  const pctConf = withConf.length > 0 ? ((conformes / withConf.length) * 100) : 0;
  const parametros = [...new Set(data.map(d => d.parametro))].length;
  const pontos = [...new Set(data.map(d => d.ponto_coleta))].length;
  const dates = data.map(d => d.data_amostragem).filter(Boolean).sort();
  const lastDate = dates[dates.length - 1];
  const daysSince = lastDate ? Math.floor((new Date() - new Date(lastDate)) / 86400000) : 999;
  const ncByParam = {};
  data.filter(d => d.is_conforme === false).forEach(d => { ncByParam[d.parametro] = (ncByParam[d.parametro] || 0) + 1; });
  const criticalParam = Object.entries(ncByParam).sort((a, b) => b[1] - a[1])[0];
  const donutData = [
    { name: "Conforme", value: conformes, color: COLORS.green },
    { name: "Não Conforme", value: naoConformes, color: COLORS.red },
  ];
  const rankingData = Object.entries(ncByParam).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([param, count]) => ({ param, count }));
  const pointData = {};
  data.filter(d => d.is_conforme !== null).forEach(d => {
    if (!pointData[d.ponto_coleta]) pointData[d.ponto_coleta] = { conforme: 0, naoConforme: 0 };
    if (d.is_conforme) pointData[d.ponto_coleta].conforme++; else pointData[d.ponto_coleta].naoConforme++;
  });
  const stackedData = Object.entries(pointData).map(([ponto, vals]) => {
    const total = vals.conforme + vals.naoConforme;
    return { ponto, Conforme: ((vals.conforme / total) * 100), "Não Conforme": ((vals.naoConforme / total) * 100) };
  });
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
        <KpiCard icon="🧪" title="Total de análises já realizadas" value={totalAnalises} bg={COLORS.primary} textColor="white" />
        <KpiCard icon="✅" title="Dentro do limite" value={conformes} subtitle={`${fmtNum(pctConf, 1)}% do total`} bg={COLORS.green} textColor="white" />
        <KpiCard icon="❌" title="Fora do limite" value={naoConformes} borderColor={COLORS.red} textColor={COLORS.red} />
        <KpiCard icon="📊" title="Indicadores monitorados" value={parametros} />
        <KpiCard icon="📍" title="Pontos de Coleta" value={pontos} />
        <KpiCard icon="📅" title="Última Coleta" value={fmtDate(lastDate)} bg={daysSince >= 45 ? COLORS.yellow : COLORS.greenLight} subtitle={daysSince >= 45 ? `⚠️ ${daysSince} dias sem coleta` : `${daysSince} dias atrás`} />
        {criticalParam && <KpiCard icon="⚠️" title="Indicador mais crítico" value={criticalParam[0]} subtitle={`${criticalParam[1]} reprovações`} bg={COLORS.redLight} textColor={COLORS.red} />}
      </div>
      <div className="rivvos-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={{ background: COLORS.white, borderRadius: 12, padding: 20, border: "1px solid #E8ECF0", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: COLORS.primary }}>Visão geral dos resultados</h3>
          <ResponsiveContainer width="100%" height={320} minHeight={320}>
            <PieChart>
              <Pie data={donutData} cx="50%" cy="50%" innerRadius={65} outerRadius={100} dataKey="value" stroke="none" paddingAngle={3}>
                {donutData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip />
              <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ textAlign: "center", top: -193, marginBottom: -50, position: "relative", zIndex: 1, pointerEvents: "none" }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: COLORS.primary }}>{fmtNum(pctConf, 1)}%</div>
            <div style={{ fontSize: 10, color: "#8899AA", textTransform: "uppercase", letterSpacing: 1 }}>Conforme</div>
          </div>
        </div>
        <div style={{ background: COLORS.white, borderRadius: 12, padding: 20, border: "1px solid #E8ECF0" }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: COLORS.primary }}>Indicadores com mais problemas</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={rankingData} layout="vertical" margin={{ left: 10, right: 60 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E8ECF0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#8899AA" }} />
              <YAxis type="category" dataKey="param" tick={{ fontSize: 11, fill: COLORS.primary }} width={130} />
              <Tooltip />
              <Bar dataKey="count" fill={COLORS.red} radius={[0, 4, 4, 0]} barSize={18} name="Ocorrências">
                <LabelList dataKey="count" position="right" style={{ fontSize: 11, fontWeight: 600, fill: COLORS.red }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div style={{ background: COLORS.white, borderRadius: 12, padding: 20, border: "1px solid #E8ECF0", marginTop: 20 }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: COLORS.primary }}>Desempenho por ponto de coleta</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={stackedData} margin={{ top: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8ECF0" vertical={false} />
            <XAxis dataKey="ponto" tick={{ fontSize: 12, fill: COLORS.primary, fontWeight: 600 }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#8899AA" }} tickFormatter={v => `${v}%`} />
            <Tooltip formatter={v => `${fmtNum(v, 1)}%`} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Conforme" stackId="a" fill={COLORS.green}>
              <LabelList dataKey="Conforme" position="center" style={{ fontSize: 11, fontWeight: 700, fill: "white" }} formatter={v => `${fmtNum(v, 0)}%`} />
            </Bar>
            <Bar dataKey="Não Conforme" stackId="a" fill={COLORS.red} radius={[4, 4, 0, 0]}>
              <LabelList dataKey="Não Conforme" position="center" style={{ fontSize: 11, fontWeight: 700, fill: "white" }} formatter={v => `${fmtNum(v, 0)}%`} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── PAGE 2: HISTORICAL ─────────────────────────────────────────────────────
function PageHistorico({ data }) {
  const paramOptions = [...new Set(data.map(d => d.parametro))].sort();
  const pontoOptions = [...new Set(data.map(d => d.ponto_coleta))].sort();
  const dateOptions = [...new Set(data.map(d => d.data_amostragem))].sort();
  const paramsWithLimit = ["Ferro dissolvido", "Manganês Total", "Oxigênio Dissolvido", "pH", "Sulfatos"];
  const [selParam, setSelParam] = useState(["Ferro dissolvido"]);
  const [selPonto, setSelPonto] = useState([]);
  const [selDateRaioX, setSelDateRaioX] = useState([dateOptions[dateOptions.length - 1] || ""]);
  const [selPontoRaioX, setSelPontoRaioX] = useState(pontoOptions.includes("Jusante") ? ["Jusante"] : [pontoOptions[0] || ""]);
  const param = selParam[0] || "Ferro dissolvido";
  const filteredEvo = data.filter(d => d.parametro === param && (selPonto.length === 0 || selPonto.includes(d.ponto_coleta)));
  const evoByDate = {};
  filteredEvo.forEach(d => {
    if (!evoByDate[d.data_amostragem]) evoByDate[d.data_amostragem] = { values: [], limMax: d.limite_max, limMin: d.limite_min };
    evoByDate[d.data_amostragem].values.push(d.valor);
  });
  const evoData = Object.entries(evoByDate).sort((a, b) => a[0].localeCompare(b[0])).map(([date, info]) => {
    const avg = info.values.reduce((a, b) => a + b, 0) / info.values.length;
    const isConf = info.limMax != null ? (param === "pH" ? (avg >= (info.limMin || 0) && avg <= info.limMax) : param === "Oxigênio Dissolvido" ? avg >= (info.limMin || 0) : avg <= info.limMax) : true;
    return { date: fmtDate(date), valor: avg, limMax: info.limMax, limMin: info.limMin, isConf };
  });
  const limiteRef = evoData[0]?.limMax;
  const limiteMinRef = evoData[0]?.limMin;
  const raioXDate = selDateRaioX[0] || dateOptions[dateOptions.length - 1];
  const raioXPonto = selPontoRaioX[0] || pontoOptions[0];
  const raioXData = data.filter(d => d.data_amostragem === raioXDate && d.ponto_coleta === raioXPonto && paramsWithLimit.includes(d.parametro)).map(d => {
    let pct = d.pct_limite;
    if (d.parametro === "Oxigênio Dissolvido" && d.limite_min != null) pct = (d.valor / d.limite_min) * 100;
    if (d.parametro === "pH" && d.limite_min != null) { if (d.valor < d.limite_min) pct = (d.valor / d.limite_min) * 100; else if (d.valor > d.limite_max) pct = (d.valor / d.limite_max) * 100; else pct = 100; }
    return { parametro: d.parametro, pct: pct || 0, valor: d.valor, limite: d.parametro === "Oxigênio Dissolvido" ? d.limite_min : d.limite_max, isConf: d.is_conforme };
  });
  const confByDate = {};
  data.filter(d => d.is_conforme !== null).forEach(d => {
    const key = d.data_amostragem;
    if (!confByDate[key]) confByDate[key] = { conformes: 0, total: 0 };
    confByDate[key].total++; if (d.is_conforme) confByDate[key].conformes++;
  });
  const confTimeline = Object.entries(confByDate).sort((a, b) => a[0].localeCompare(b[0])).map(([date, info]) => ({ date: fmtDate(date), pct: (info.conformes / info.total) * 100 }));
  return (
    <div>
      <div style={{ background: COLORS.white, borderRadius: 12, padding: 20, border: "1px solid #E8ECF0", marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: COLORS.primary }}>Evolução Histórica — {param}</h3>
          <div style={{ display: "flex", gap: 10 }}>
            <MultiSelect label="Parâmetro" options={paramOptions} selected={selParam} onChange={setSelParam} multi={false} />
            <MultiSelect label="Ponto de Coleta" options={pontoOptions} selected={selPonto} onChange={setSelPonto} />
          </div>
        </div>
        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart data={evoData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8ECF0" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8899AA" }} />
            <YAxis tick={{ fontSize: 11, fill: "#8899AA" }} tickFormatter={v => fmtNum(v)} />
            <Tooltip content={({ active, payload, label }) => { if (!active || !payload?.length) return null; const d = payload[0]?.payload; return (<div style={{ background: "rgba(27,42,74,0.95)", borderRadius: 8, padding: "10px 14px", color: "white", fontSize: 12 }}><div style={{ fontWeight: 600 }}>{label}</div><div>Valor: {fmtNum(d?.valor, 3)}</div>{d?.limMax && <div>Limite Máx: {fmtNum(d.limMax, 2)}</div>}{d?.limMin && <div>Limite Mín: {fmtNum(d.limMin, 2)}</div>}<div style={{ color: d?.isConf ? "#4ADE80" : "#F87171" }}>{d?.isConf ? "✓ Conforme" : "✗ Não Conforme"}</div></div>); }} />
            <Bar dataKey="valor" name="Valor Médio" barSize={36} radius={[4, 4, 0, 0]}>
              {evoData.map((entry, i) => <Cell key={i} fill={entry.isConf ? COLORS.green : COLORS.red} />)}
            </Bar>
            {limiteRef && <ReferenceLine y={limiteRef} stroke={COLORS.red} strokeDasharray="8 4" strokeWidth={2} label={{ value: `Limite: ${fmtNum(limiteRef)}`, position: "insideTopRight", fontSize: 11, fill: COLORS.red }} />}
            {limiteMinRef && param !== "Oxigênio Dissolvido" && <ReferenceLine y={limiteMinRef} stroke="#E67E22" strokeDasharray="8 4" strokeWidth={2} label={{ value: `Mín: ${fmtNum(limiteMinRef)}`, position: "insideBottomRight", fontSize: 11, fill: "#E67E22" }} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="rivvos-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={{ background: COLORS.white, borderRadius: 12, padding: 20, border: "1px solid #E8ECF0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: COLORS.primary }}>Raio-X da Coleta</h3>
            <div style={{ display: "flex", gap: 10 }}>
              <MultiSelect label="Data" options={dateOptions} selected={selDateRaioX} onChange={setSelDateRaioX} multi={false} />
              <MultiSelect label="Ponto" options={pontoOptions} selected={selPontoRaioX} onChange={setSelPontoRaioX} multi={false} />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={raioXData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8ECF0" vertical={false} />
              <XAxis dataKey="parametro" tick={{ fontSize: 10, fill: COLORS.primary }} interval={0} angle={-15} textAnchor="end" />
              <YAxis tick={{ fontSize: 11, fill: "#8899AA" }} tickFormatter={v => `${v}%`} />
              <Tooltip content={({ active, payload }) => { if (!active || !payload?.length) return null; const d = payload[0]?.payload; return (<div style={{ background: "rgba(27,42,74,0.95)", borderRadius: 8, padding: "10px 14px", color: "white", fontSize: 12 }}><div style={{ fontWeight: 600 }}>{d?.parametro}</div><div>Valor: {fmtNum(d?.valor, 3)}</div><div>Limite: {fmtNum(d?.limite, 2)}</div><div>% do Limite: {fmtNum(d?.pct, 1)}%</div></div>); }} />
              <ReferenceLine y={100} stroke={COLORS.red} strokeDasharray="6 3" strokeWidth={2} label={{ value: "100% — Limite Legal", position: "insideTopRight", fontSize: 10, fill: COLORS.red }} />
              <Bar dataKey="pct" name="% do Limite" barSize={32} radius={[4, 4, 0, 0]}>
                {raioXData.map((entry, i) => <Cell key={i} fill={entry.pct >= 100 ? COLORS.red : COLORS.green} />)}
                <LabelList dataKey="pct" position="top" style={{ fontSize: 10, fontWeight: 600 }} formatter={v => `${fmtNum(v, 0)}%`} />
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background: COLORS.white, borderRadius: 12, padding: 20, border: "1px solid #E8ECF0" }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: COLORS.primary }}>Índice de Conformidade no Tempo</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={confTimeline} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8ECF0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8899AA" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#8899AA" }} tickFormatter={v => `${v}%`} />
              <Tooltip content={({ active, payload, label }) => { if (!active || !payload?.length) return null; return (<div style={{ background: "rgba(27,42,74,0.95)", borderRadius: 8, padding: "10px 14px", color: "white", fontSize: 12 }}><div style={{ fontWeight: 600 }}>{label}</div><div>Conformidade: {fmtNum(payload[0].value, 1)}%</div></div>); }} />
              <ReferenceLine y={80} stroke={COLORS.green} strokeDasharray="6 3" label={{ value: "Meta 80%", position: "insideTopLeft", fontSize: 10, fill: COLORS.green }} />
              <ReferenceLine y={60} stroke="#F39C12" strokeDasharray="6 3" label={{ value: "Alerta 60%", position: "insideBottomLeft", fontSize: 10, fill: "#F39C12" }} />
              <Line type="monotone" dataKey="pct" stroke={COLORS.primary} strokeWidth={3} dot={{ fill: COLORS.primary, r: 5 }} activeDot={{ r: 7 }} name="% Conformidade" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ─── PAGE 3: ALERTS ─────────────────────────────────────────────────────────
function PageAlertas({ data }) {
  const [alertPage, setAlertPage] = useState(0);
  const [histPage, setHistPage] = useState(0);
  const [histParam, setHistParam] = useState([]);
  const [histPonto, setHistPonto] = useState([]);
  const [histConf, setHistConf] = useState([]);
  const PAGE_SIZE = 25;
  const alerts = data.filter(d => d.is_conforme === false).sort((a, b) => b.data_amostragem.localeCompare(a.data_amostragem));
  const alertPages = Math.ceil(alerts.length / PAGE_SIZE);
  const alertSlice = alerts.slice(alertPage * PAGE_SIZE, (alertPage + 1) * PAGE_SIZE);
  const histFiltered = data.filter(d => {
    if (histParam.length > 0 && !histParam.includes(d.parametro)) return false;
    if (histPonto.length > 0 && !histPonto.includes(d.ponto_coleta)) return false;
    if (histConf.length > 0) { if (histConf.includes("Conforme") && d.is_conforme !== true) return false; if (histConf.includes("Não Conforme") && d.is_conforme !== false) return false; }
    return true;
  }).sort((a, b) => b.data_amostragem.localeCompare(a.data_amostragem));
  const histPages = Math.ceil(histFiltered.length / PAGE_SIZE);
  const histSlice = histFiltered.slice(histPage * PAGE_SIZE, (histPage + 1) * PAGE_SIZE);
  const paramOpts = [...new Set(data.map(d => d.parametro))].sort();
  const pontoOpts = [...new Set(data.map(d => d.ponto_coleta))].sort();
  const thStyle = { padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: COLORS.primary, borderBottom: `2px solid ${COLORS.primary}`, position: "sticky", top: 0, background: COLORS.white, textTransform: "uppercase", letterSpacing: 0.5 };
  const tdStyle = { padding: "8px 12px", fontSize: 12, borderBottom: "1px solid #E8ECF0" };
  const Badge = ({ conf }) => {
    if (conf === true) return <span style={{ background: COLORS.greenLight, color: COLORS.green, padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>Conforme</span>;
    if (conf === false) return <span style={{ background: COLORS.redLight, color: COLORS.red, padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>Não Conforme</span>;
    return <span style={{ color: "#AAA", fontSize: 10 }}>—</span>;
  };
  const Pagination = ({ page, total, setPage }) => (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 12 }}>
      <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #D5DDE5", background: page === 0 ? "#F5F5F5" : "white", cursor: page === 0 ? "default" : "pointer", fontSize: 12 }}>‹ Anterior</button>
      <span style={{ fontSize: 12, color: "#8899AA" }}>Página {page + 1} de {total || 1}</span>
      <button onClick={() => setPage(Math.min(total - 1, page + 1))} disabled={page >= total - 1} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #D5DDE5", background: page >= total - 1 ? "#F5F5F5" : "white", cursor: page >= total - 1 ? "default" : "pointer", fontSize: 12 }}>Próxima ›</button>
    </div>
  );
  return (
    <div>
      <div style={{ background: COLORS.white, borderRadius: 12, padding: 20, border: "1px solid #E8ECF0", marginBottom: 24 }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: COLORS.red }}>⚠️ Alertas Ativos — Não Conformidades ({alerts.length})</h3>
        <div style={{ overflowX: "auto", maxHeight: 500, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thStyle}>Data</th><th style={thStyle}>Ponto</th><th style={thStyle}>Parâmetro</th><th style={thStyle}>Valor</th><th style={thStyle}>Limite</th><th style={thStyle}>% Excedido</th><th style={thStyle}>Legislação</th></tr></thead>
            <tbody>
              {alertSlice.map((d, i) => (
                <tr key={i} style={{ background: COLORS.redLight }}>
                  <td style={tdStyle}>{fmtDate(d.data_amostragem)}</td>
                  <td style={tdStyle}>{d.ponto_coleta}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{d.parametro}</td>
                  <td style={{ ...tdStyle, color: COLORS.red, fontWeight: 700 }}>{fmtNum(d.valor, 3)}</td>
                  <td style={tdStyle}>{d.limite_max != null ? fmtNum(d.limite_max, 2) : (d.limite_min != null ? fmtNum(d.limite_min, 2) : "—")}</td>
                  <td style={{ ...tdStyle, color: COLORS.red, fontWeight: 700 }}>{d.pct_excedido > 0 ? `+${fmtNum(d.pct_excedido, 1)}%` : "—"}</td>
                  <td style={{ ...tdStyle, fontSize: 10 }}>{d.legislacao}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={alertPage} total={alertPages} setPage={setAlertPage} />
      </div>
      <div style={{ background: COLORS.white, borderRadius: 12, padding: 20, border: "1px solid #E8ECF0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: COLORS.primary }}>Histórico Completo ({histFiltered.length} registros)</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <MultiSelect label="Parâmetro" options={paramOpts} selected={histParam} onChange={(v) => { setHistParam(v); setHistPage(0); }} />
            <MultiSelect label="Ponto" options={pontoOpts} selected={histPonto} onChange={(v) => { setHistPonto(v); setHistPage(0); }} />
            <MultiSelect label="Conformidade" options={["Conforme", "Não Conforme"]} selected={histConf} onChange={(v) => { setHistConf(v); setHistPage(0); }} multi={false} />
          </div>
        </div>
        <div style={{ overflowX: "auto", maxHeight: 500, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thStyle}>Nº Relatório</th><th style={thStyle}>Data</th><th style={thStyle}>Ponto</th><th style={thStyle}>Parâmetro</th><th style={thStyle}>Resultado</th><th style={thStyle}>Unidade</th><th style={thStyle}>Limite Máx</th><th style={thStyle}>Conformidade</th><th style={thStyle}>Legislação</th></tr></thead>
            <tbody>
              {histSlice.map((d, i) => (
                <tr key={i} style={{ background: d.is_conforme === false ? COLORS.redLight : "transparent" }}>
                  <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: 11 }}>{d.numero_relatorio}</td>
                  <td style={tdStyle}>{fmtDate(d.data_amostragem)}</td>
                  <td style={tdStyle}>{d.ponto_coleta}</td>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{d.parametro}</td>
                  <td style={tdStyle}>{d.resultado}</td>
                  <td style={{ ...tdStyle, color: "#8899AA" }}>{d.unidade || "—"}</td>
                  <td style={tdStyle}>{d.limite_max != null ? fmtNum(d.limite_max, 2) : "—"}</td>
                  <td style={tdStyle}><Badge conf={d.is_conforme} /></td>
                  <td style={{ ...tdStyle, fontSize: 10 }}>{d.legislacao}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={histPage} total={histPages} setPage={setHistPage} />
      </div>
    </div>
  );
}

// ─── LANDING PAGE (no client selected) ──────────────────────────────────────
function LandingPage({ error }) {
  return (
    <div style={{ fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif", background: COLORS.bgLight, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 480, padding: 40 }}>
        <div style={{ width: 64, height: 64, borderRadius: 14, background: COLORS.primary, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800, color: "white", marginBottom: 20 }}>R</div>
        <h1 style={{ margin: "0 0 8px", fontSize: 28, fontWeight: 800, color: COLORS.primary }}>Rivvos</h1>
        <p style={{ margin: "0 0 24px", fontSize: 14, color: "#8899AA" }}>Monitoramento Ambiental Inteligente</p>
        {error ? (
          <div style={{ background: COLORS.redLight, border: `1px solid ${COLORS.red}`, borderRadius: 10, padding: 20 }}>
            <p style={{ margin: 0, fontSize: 14, color: COLORS.red }}>{error}</p>
          </div>
        ) : (
          <div style={{ background: COLORS.white, borderRadius: 10, padding: 20, border: "1px solid #E8ECF0" }}>
            <p style={{ margin: 0, fontSize: 14, color: "#6B7280" }}>
              Acesse o link fornecido pela equipe Rivvos para visualizar seu dashboard de monitoramento ambiental.
            </p>
          </div>
        )}
        <p style={{ marginTop: 32, fontSize: 11, color: "#AAB5C0" }}>Água Superficial — CONAMA 357/2005</p>
      </div>
    </div>
  );
}

// ─── LOADING SCREEN ─────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif", background: COLORS.bgLight, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 48, height: 48, border: `4px solid #E8ECF0`, borderTop: `4px solid ${COLORS.primary}`, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
        <p style={{ color: "#8899AA", fontSize: 14 }}>Carregando dados...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

// ─── MAIN APP ───────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("executivo");
  const [filterAno, setFilterAno] = useState([]);
  const [filterPonto, setFilterPonto] = useState([]);
  const [filterParam, setFilterParam] = useState([]);
  const [filterMatriz, setFilterMatriz] = useState([]);
  const [allData, setAllData] = useState([]);
  const [clienteNome, setClienteNome] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      const token = getClientToken();

      if (!token) {
        setLoading(false);
        return;
      }

      if (!supabase) {
        setError("Sistema não configurado. Entre em contato com o administrador.");
        setLoading(false);
        return;
      }

      try {
        // 1. Look up client by UUID token
        const { data: clienteData, error: clienteError } = await supabase
          .from("clientes")
          .select("slug, nome")
          .eq("token", token)
          .eq("ativo", true)
          .single();

        if (clienteError || !clienteData) {
          setError("Link inválido ou cliente não encontrado. Verifique o link fornecido.");
          setLoading(false);
          return;
        }

        setClienteNome(clienteData.nome);

        // 2. Fetch analysis data for this client
        const { data: analises, error: analiseError } = await supabase
          .from("analises_agua_superficial")
          .select("*")
          .eq("cliente", clienteData.slug)
          .order("data_amostragem", { ascending: true });

        if (analiseError) {
          setError("Erro ao carregar dados. Tente novamente mais tarde.");
          setLoading(false);
          return;
        }

        if (!analises || analises.length === 0) {
          setError("Nenhum dado encontrado para este cliente ainda.");
          setLoading(false);
          return;
        }

        // 3. Clean and set data
        setAllData(analises.map(cleanRow));
      } catch (err) {
        console.error("Erro:", err);
        setError("Erro de conexão. Verifique sua internet e tente novamente.");
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  const anos = [...new Set(allData.map(d => d.ano))].sort();
  const pontos = [...new Set(allData.map(d => d.ponto_coleta))].sort();
  const params = [...new Set(allData.map(d => d.parametro))].sort();
  const matrizes = [...new Set(allData.map(d => d.tipo_matriz).filter(Boolean))].sort();

  const filteredData = useMemo(() => {
    return allData.filter(d => {
      if (filterAno.length > 0 && !filterAno.includes(String(d.ano))) return false;
      if (filterPonto.length > 0 && !filterPonto.includes(d.ponto_coleta)) return false;
      if (filterParam.length > 0 && !filterParam.includes(d.parametro)) return false;
      if (filterMatriz.length > 0 && !filterMatriz.includes(d.tipo_matriz)) return false;
      return true;
    });
  }, [allData, filterAno, filterPonto, filterParam, filterMatriz]);

  // ─── RENDER STATES ──────────────────────────────────────────────────────
  if (loading) return <LoadingScreen />;
  if (!getClientToken() || error) return <LandingPage error={error} />;
  if (allData.length === 0) return <LandingPage error="Nenhum dado disponível." />;

  const navItems = [
    { key: "executivo", label: "Visão Executiva", icon: "📊" },
    { key: "historico", label: "Série Histórica", icon: "📈" },
    { key: "alertas", label: "Alertas e Dados", icon: "⚠️" },
  ];

  return (
    <div style={{ fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif", background: COLORS.bgLight, minHeight: "100vh" }}>
      <style>{`
        @media (max-width: 768px) {
          .rivvos-grid-2col { grid-template-columns: 1fr !important; }
          .rivvos-nav { display: none !important; }
          .rivvos-nav-mobile { display: flex !important; }
          .rivvos-filters { flex-direction: column; }
          .rivvos-header-inner { flex-direction: column; align-items: flex-start !important; }
        }
      `}</style>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${COLORS.primary} 0%, #2C3E6D 100%)`, padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 8, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800, color: "white" }}>R</div>
          <div>
            <div style={{ color: "white", fontSize: 16, fontWeight: 700 }}>Rivvos — Monitoramento Ambiental</div>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>{clienteNome || "Água Superficial — CONAMA 357/2005"}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {navItems.map(n => (
            <button key={n.key} onClick={() => setPage(n.key)} style={{ padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, background: page === n.key ? "rgba(255,255,255,0.2)" : "transparent", color: page === n.key ? "white" : "rgba(255,255,255,0.6)", transition: "all 0.2s" }}>
              <span style={{ marginRight: 6 }}>{n.icon}</span>{n.label}
            </button>
          ))}
        </div>
      </div>
      {/* Filters */}
      <div style={{ background: COLORS.white, padding: "10px 24px", borderBottom: "1px solid #E8ECF0", display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <MultiSelect label="Ano" options={anos.map(String)} selected={filterAno} onChange={setFilterAno} />
        <MultiSelect label="Ponto de Coleta" options={pontos} selected={filterPonto} onChange={setFilterPonto} />
        <MultiSelect label="Parâmetro" options={params} selected={filterParam} onChange={setFilterParam} />
        {matrizes.length > 1 && <MultiSelect label="Tipo de Matriz" options={matrizes} selected={filterMatriz} onChange={setFilterMatriz} />}
        {(filterAno.length > 0 || filterPonto.length > 0 || filterParam.length > 0 || filterMatriz.length > 0) && (
          <button onClick={() => { setFilterAno([]); setFilterPonto([]); setFilterParam([]); setFilterMatriz([]); }} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #D5DDE5", background: "#FAFAFA", cursor: "pointer", fontSize: 11, color: "#888" }}>✕ Limpar filtros</button>
        )}
        <div style={{ marginLeft: "auto", fontSize: 11, color: "#8899AA", paddingBottom: 6 }}>{filteredData.length} registros</div>
      </div>
      {/* Content */}
      <div style={{ padding: 24, maxWidth: 1280, margin: "0 auto" }}>
        {page === "executivo" && <PageExecutive data={filteredData} />}
        {page === "historico" && <PageHistorico data={filteredData} />}
        {page === "alertas" && <PageAlertas data={filteredData} />}
      </div>
      {/* Footer */}
      <div style={{ textAlign: "center", padding: "16px 24px", color: "#AAB5C0", fontSize: 10, borderTop: "1px solid #E8ECF0" }}>
        Rivvos — Monitoramento Ambiental © {new Date().getFullYear()} · Dados conforme CONAMA 357/2005
      </div>
    </div>
  );
}
